/**
 * Login brand panel — deep-blue gradient (the brand structural ink) with a warm
 * orange glow + orange numbered captions for the accent pop, an inverted SVG wordmark,
 * the product promise, and the three data planes as numbered micro-rows (echoes
 * the dashboard's numbered-widget captions). Decorative only: hidden below `lg`
 * and excluded from the accessibility tree where appropriate.
 */

const DATA_PLANES = [
  {
    no: '01',
    label: 'Inventory',
    line: 'Every identity, role, and key across AWS, GCP, and Azure.',
  },
  {
    no: '02',
    label: 'Activity',
    line: 'Audit events correlated into per-principal timelines.',
  },
  {
    no: '03',
    label: 'Governance',
    line: 'Access requests, compliance flags, board-ready reports.',
  },
] as const

export function BrandPanel() {
  return (
    <div
      className="hidden lg:flex"
      style={{
        position: 'relative',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: 'var(--s-12)',
        overflow: 'hidden',
        background:
          'linear-gradient(150deg, var(--brand-700) 0%, var(--brand-800) 55%, var(--brand-900) 100%)',
      }}
    >
      <BrandGlow />
      {/* Intro rides vertically centered in the space above the data planes. */}
      <div
        style={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <BrandIntro />
      </div>
      <DataPlanes />
    </div>
  )
}

/** Soft radial highlight — decorative, token-derived. */
function BrandGlow() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        background:
          'radial-gradient(ellipse 70% 50% at 85% 8%, color-mix(in oklab, var(--accent-400) 30%, transparent), transparent 70%)',
        pointerEvents: 'none',
      }}
    />
  )
}

function BrandIntro() {
  return (
    <div style={{ position: 'relative' }}>
      <img
        src="/assets/logos/logo-wordmark-black.svg"
        alt="IntegraTrace"
        style={{
          width: 'min(260px, 60%)',
          // The wordmark ships black; invert it to read on the navy field.
          filter: 'brightness(0) invert(1)',
          opacity: 0.96,
        }}
      />
      <p
        style={{
          marginTop: 'var(--s-6)',
          maxWidth: 420,
          fontFamily: 'var(--ff-display)',
          fontSize: 'var(--fs-28)',
          fontWeight: 'var(--fw-semibold)',
          lineHeight: 'var(--lh-snug)',
          letterSpacing: 'var(--tracking-snug)',
          color: 'var(--brand-50)',
        }}
      >
        See every identity. Across every cloud. All the time.
      </p>
    </div>
  )
}

function DataPlanes() {
  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
      {DATA_PLANES.map((p) => (
        <DataPlaneRow key={p.no} no={p.no} label={p.label} line={p.line} />
      ))}
      <div
        className="mono"
        style={{
          marginTop: 'var(--s-6)',
          paddingTop: 'var(--s-4)',
          borderTop: '1px solid color-mix(in oklab, var(--accent-200) 25%, transparent)',
          display: 'flex',
          gap: 'var(--s-4)',
          color: 'var(--accent-300)',
          fontSize: 'var(--fs-11)',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}
      >
        <span>SOC 2 Type II</span>
        <span aria-hidden="true">·</span>
        <span>Continuous assurance</span>
      </div>
    </div>
  )
}

function DataPlaneRow({ no, label, line }: { no: string; label: string; line: string }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--s-3)', alignItems: 'baseline' }}>
      <span className="mono" style={{ color: 'var(--accent-300)', fontSize: 'var(--fs-11)' }}>
        {no}
      </span>
      <div>
        <div className="kicker" style={{ color: 'var(--accent-200)', letterSpacing: '0.14em' }}>
          {label}
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 'var(--fs-13)',
            lineHeight: 'var(--lh-normal)',
            color: 'color-mix(in oklab, var(--brand-100) 90%, transparent)',
          }}
        >
          {line}
        </div>
      </div>
    </div>
  )
}
